import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import useAssignmentChatThread from 'hooks/chat/useAssignmentChatThread';
import { useCallback, useMemo } from 'react';
import chatService from 'services/chat';
import {
    buildStudentFeedbackChatViewModel,
    buildSubmissionCreatedSystemMessage,
    hasSubmissionCreatedSystemMessage,
} from 'utils/chat';

const sortMessagesByCreatedOn = (messages = []) => {
    return [...messages].sort((left, right) => {
        const leftCreatedOn = left?.created_on || 0;
        const rightCreatedOn = right?.created_on || 0;

        if (leftCreatedOn !== rightCreatedOn) {
            return leftCreatedOn - rightCreatedOn;
        }

        return (left?.modified_on || leftCreatedOn) - (right?.modified_on || rightCreatedOn);
    });
};

const buildSubmissionReference = (submission) => {
    return {
        id: submission?.id,
        submission_id: submission?.id,
    };
};

const buildExpectedSubmissionCreatedSystemMessages = (submissions = []) => {
    const sortedSubmissions = [...submissions].sort((left, right) => {
        return (left?.created_on || 0) - (right?.created_on || 0);
    });

    return sortedSubmissions.map((submission, index) => {
        return buildSubmissionCreatedSystemMessage(submission, index);
    });
};

const useStudentSubmissionChat = ({
    currentSubmission,
    isOpen,
    onChatUpdated,
    shouldShowChatSuggestions,
}) => {
    const { currentAssignment, studentSubmissions } = useApp();
    const { auth } = useAuth();

    const studentChatViewModel = useMemo(() => buildStudentFeedbackChatViewModel(), []);
    const currentSubmissionFileKey = currentSubmission?.files?.[0]?.key;
    const currentSubmissionReference = useMemo(() => buildSubmissionReference(currentSubmission), [currentSubmission]);
    const expectedSubmissionCreatedSystemMessages = useMemo(() => {
        return buildExpectedSubmissionCreatedSystemMessages(studentSubmissions || []);
    }, [studentSubmissions]);

    const reconcileSubmissionCreatedSystemMessages = useCallback(
        async (assignmentChat) => {
            if (!assignmentChat || expectedSubmissionCreatedSystemMessages.length === 0) return assignmentChat;

            let nextAssignmentChat = assignmentChat;

            for (const submissionCreatedMessage of expectedSubmissionCreatedSystemMessages) {
                const hasSystemMessage = hasSubmissionCreatedSystemMessage({
                    chat: nextAssignmentChat,
                    draftNumber: submissionCreatedMessage.system_data?.draftNumber,
                    messageId: submissionCreatedMessage.id,
                });

                if (hasSystemMessage) continue;

                const pushMessageResponse = await chatService.pushMessage({
                    auth_token: `Bearer ${localStorage.getItem('auth_token')}`,
                    chat_id: nextAssignmentChat.id,
                    message: submissionCreatedMessage,
                });

                const reconciledMessage = pushMessageResponse?.message || submissionCreatedMessage;
                const isMessageAlreadyInLoadedPage = (nextAssignmentChat.messages || []).some(
                    ({ id }) => id === reconciledMessage.id
                );

                if (pushMessageResponse?.duplicated && !isMessageAlreadyInLoadedPage) {
                    continue;
                }

                nextAssignmentChat = {
                    ...nextAssignmentChat,
                    modified_on: reconciledMessage.modified_on || nextAssignmentChat.modified_on,
                    messages: sortMessagesByCreatedOn([
                        ...(nextAssignmentChat.messages || []),
                        reconciledMessage,
                    ]),
                };
            }

            return nextAssignmentChat;
        },
        [expectedSubmissionCreatedSystemMessages]
    );

    const requestChatSuggestions = useCallback(() => {
        return chatService.getSuggestions({
            auth_token: `Bearer ${localStorage.getItem('auth_token')}`,
            assignment: currentAssignment,
            submission: currentSubmissionReference,
            count: 3,
        });
    }, [currentAssignment, currentSubmissionReference]);

    const assignmentChatThread = useAssignmentChatThread({
        assignmentId: currentAssignment?.id,
        courseId: auth?.course_id,
        ownerAccountId: auth?.id,
        readerAccountId: auth?.owner_account_id || auth?.id,
        createSuggestionsRequest: requestChatSuggestions,
        initializeChat: reconcileSubmissionCreatedSystemMessages,
        isOpen,
        onChatUpdated,
        shouldShowSuggestions: shouldShowChatSuggestions,
    });

    return {
        ...assignmentChatThread,
        fileKey: currentSubmissionFileKey,
        viewModel: studentChatViewModel,
    };
};

export default useStudentSubmissionChat;
